import { IUserNotificationService } from '@dirootie-web/components-types';
import { application } from '../../_runtime/bootstrap';
import { clone } from 'ramda';

type ActionRowProps = {
  endpoint: string;
  props: {
    row: any;
    userNotifications: IUserNotificationService;
    tableContext: any;
  };
};

/**
 * Function: deleteTableRow
 *
 * Deletes a table row by sending a DELETE request to the specified endpoint with the row's id.
 * It then refetches the table data upon success or shows an error notification upon failure.
 *
 * @param {object} params - The parameters for deleting the row.
 * @param {string} params.endpoint - The endpoint to send the DELETE request to.
 * @param {ActionRowProps} params.props - The properties containing the row data and context.
 * @param {object} params.props.row - The row data to be deleted.
 * @param {object} params.props.userNotifications - The notification service for displaying error messages.
 * @param {object} params.props.tableContext - The table context for refetching data after deletion.
 */
export const deleteTableRow = ({
  endpoint,
  props,
  onClose,
}: ActionRowProps & { onClose?: () => void }) => {
  const { row, tableContext } = props;
  application
    .getHttpClient()
    .delete(`${endpoint}/${row.id}`)
    .then(() => {
      tableContext.refetch();
      if (onClose) onClose();
    })
    .catch((error: any) => {
      if (onClose) onClose();
    });
};

/**
 * Function: downloadFile
 *
 * Initiates a file download by creating a temporary anchor element, setting its href to the provided URL,
 * and programmatically clicking it. This triggers the browser's download functionality.
 *
 * @param {string} url - The URL of the file to be downloaded.
 * @param {string} filename - The name to give the downloaded file.
 */
export const downloadFile = (url: string, filename: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Function: duplicateTableRow
 *
 * Duplicates a table row by copying its data and creating a new row with "(copy)" appended to the name.
 * It sends a POST request to the specified endpoint with the duplicated data.
 *
 * @param {object} params - The parameters for duplicating the row.
 * @param {string} params.endpoint - The endpoint to send the POST request to.
 * @param {DeleteRowProps} params.props - The properties containing the row data and context.
 * @param {object} params.props.row - The row data to be duplicated.
 * @param {object} params.props.userNotifications - The notification service for displaying error messages.
 * @param {object} params.props.tableContext - The table context for refetching data after duplication.
 */
export const duplicateTableRow = ({ endpoint, props }: ActionRowProps) => {
  const { row, userNotifications, tableContext } = props;
  const newItems = clone(row);
  delete newItems.id;
  newItems.name = `${newItems.name} (copy)`;

  application
    .getHttpClient()
    .post(endpoint, newItems)
    .then(() => tableContext.refetch())
    .catch((error: any) =>
      userNotifications.error({ title: error.response.data.detail })
    );
};

/**
 * Function: deleteTableRow
 *
 * Duplicates a table row through an endpoint in the back-end.
 * It sends a POST request to the specified endpoint with the data to be duplicated.
 *
 * @param {object} params - The parameters for deleting the row.
 * @param {string} params.endpoint - The endpoint to send the DELETE request to.
 * @param {ActionRowProps} params.props - The properties containing the row data and context.
 * @param {object} params.props.row - The row data to be deleted.
 * @param {object} params.props.userNotifications - The notification service for displaying error messages.
 * @param {object} params.props.tableContext - The table context for refetching data after deletion.
 */
export const cloneTableRowByEndpoint = ({
  endpoint,
  props,
  onClose,
}: ActionRowProps & { onClose?: () => void }) => {
  const { row, userNotifications, tableContext } = props;
  application
    .getHttpClient()
    .post(`${endpoint}/${row.id}`, null)
    .then(() => {
      tableContext.refetch();
      if (onClose) onClose();
    })
    .catch((error: any) =>
      userNotifications.error({ title: error.response.data.detail })
    );
};
